import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import ReactPaginate from "react-paginate";
import { formatDistanceStrict } from "date-fns";
import { IStudentNotification } from "../models/student/studentNotification";
import { INotifications } from "../models/master/notification";
import {
  setNotifications,
  setPaginationNotification,
} from "../store/slices/master/notificationSlice";
import { setNotificationType } from "../store/slices/staff/staffNotificationSlice";
import {
  getNotifications,
  getNotificationType,
  updateNotificationStatus,
} from "../action/masters/notificationActionCreator";
import { getSnapShots } from "../action/masters/snapShotActionCreator";
import LoaderSpinner from "../common-components/loader";
import FloatingLabelDropDown from "./floatingdropdown";
import { baseURL } from "../constants/commonConstants";
import { pageBlockId } from "../constants/pageBlocks";
import { getPageBlockData } from "../common-functions/commonFunctions";
import { UserTypes } from "../models/dashboard/adminDashboard";

const Notifications = (props: IStudentNotification) => {
  const {
    getNotificationType,
    notificationType,
    updateNotificationStatus,
    getPaginationNotification,
    paginationNotification,
    notViewedCount,
    snapShots,
    currentUser,
    getSnapShot,
  } = props;
  const dispatch = useDispatch();
  const [selectedNotificationId, setSelectedNotificationId] = useState<
    number | undefined
  >(undefined);
  const [viewedNotification, setViewedNotification] = useState<
    (number | null)[]
  >([]);
  const viewedNotificationRef = useRef<(number | null)[]>([]);
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const pageSize = 10;
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const getUserPageBlockData = useMemo(() => {
    switch (currentUser?.currentRole as unknown as UserTypes) {
      case UserTypes.Student:
        return pageBlockId.studentNotification;
      case UserTypes.Staff:
        return pageBlockId.staffNotification;
      case UserTypes.Faculty:
        return pageBlockId.facultyNotification;
      default:
        return 0;
    }
  }, [currentUser?.currentRole, snapShots, pageBlockId]);

  const userPageBlockData = getUserPageBlockData ?? null;
  const pageBlockData = getPageBlockData(snapShots, userPageBlockData);

  const getAllNotification = useCallback(async () => {
    setIsLoading(true);
    if (pageBlockData && pageBlockData?.displayOrNot) {
      try {
        await getPaginationNotification(
          undefined,
          userPageBlockData,
          pageSize,
          selectedNotificationId,
          selectedPage
        );
      } finally {
        setIsLoading(false);
      }
    }
  }, [
    getPaginationNotification,
    selectedNotificationId,
    selectedPage,
    pageBlockData,
  ]);

  const handlePageClick = (pageNumber: any) => {
    const newPage = pageNumber.selected + 1;
    setSelectedPage(newPage);
  };

  useEffect(() => {
    if (paginationNotification || selectedNotificationId === undefined) {
      setTotalRecord(paginationNotification?.totalCount);
    }
  }, [paginationNotification, selectedNotificationId]);

  useEffect(() => {
    const unviewedNotifications = paginationNotification?.notifications
      ?.filter((notification: INotifications) => notification.isViewed != true)
      .map((notification: INotifications) => notification.id);
    setViewedNotification(unviewedNotifications || []);
    viewedNotificationRef.current = viewedNotification || [];
  }, [paginationNotification]);

  useEffect(() => {
    return () => {
      if (
        viewedNotification &&
        viewedNotification.some((v) => v !== null && v > 0)
      ) {
        updateNotificationStatus(viewedNotification);
      }
    };
  }, [viewedNotification]);

  useEffect(() => {
    getAllNotification();
  }, [pageBlockData, selectedNotificationId]);

  useEffect(() => {
    getSnapShot();
    getNotificationType();
    if (selectedNotificationId) {
      setTotalRecord(paginationNotification?.totalCount);
    }
    return () => {
      dispatch(setPaginationNotification([]));
      dispatch(setNotificationType([]));
      dispatch(setNotifications([]));
    };
  }, []);

  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setSelectedNotificationId(Number(event.target.value));
  };

  return (
    <div
      className={
        pageBlockData?.displayOrNot
          ? "notificationwrap card-wrap h-auto"
          : "notificationwrap card-wrap h-auto card-snapshot"
      }
      style={
        pageBlockData?.displayOrNot === false
          ? { backgroundImage: `url(${baseURL + pageBlockData.image})` }
          : {}
      }
    >
      <div className="title">
        <h2>Notifications</h2>
        {pageBlockData?.displayOrNot &&
          (paginationNotification?.notifications?.length > 0 ||
            selectedNotificationId != undefined) && (
            <div className="col-md-3 form-group">
              <FloatingLabelDropDown
                label={""}
                onChange={onChange}
                isMandatory={false}
                value={selectedNotificationId}
                name={"departmentId"}
                optionsList={notificationType}
                placeholder="Select Notification Type"
              />
            </div>
          )}
      </div>
      {pageBlockData?.displayOrNot &&
        (isLoading ? (
          <LoaderSpinner />
        ) : paginationNotification?.notifications?.length > 0 ? (
          paginationNotification.notifications.map(
            (notification: INotifications, index: number) => (
              <div
                key={notification.id}
                style={{
                  backgroundColor: index % 2 === 0 ? "#b6d6ff" : "#BFE5F0",
                }}
                className={
                  notification.isViewed ? "notfy-card" : "notfy-card unread"
                }
              >
                <div className="d-flex justify-content-between mb-2">
                  <div className="category">
                    {notification.notificationTypeName}
                  </div>
                  <div className="date">
                    {formatDistanceStrict(
                      new Date(notification.createdOn),
                      new Date(),
                      { addSuffix: true }
                    )}
                  </div>
                </div>
                <h6>{notification.title}</h6>
                <p>{notification.notificationMessage}</p>
                {notification?.image && (
                  <img
                    src={baseURL + notification?.image}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.src =
                        "/assets/images/icon/graduation.svg";
                    }}
                  />
                )}
              </div>
            )
          )
        ) : (
          <p>No Records Found</p>
        ))}
      <div className="pagination">
        {totalRecord > 10 && totalRecord && (
          <ReactPaginate
            previousLabel={"←"}
            nextLabel={"→"}
            breakLabel={"..."}
            breakClassName={"break-me"}
            pageCount={totalRecord > 0 ? Math.ceil(totalRecord / pageSize) : 1}
            marginPagesDisplayed={1}
            pageRangeDisplayed={2}
            onPageChange={handlePageClick}
            containerClassName={"pagination"}
            activeClassName={"active"}
            forcePage={selectedPage - 1}
          />
        )}
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  getNotificationType: () => getNotificationType(),
  getSnapShot: () => getSnapShots(),
  updateNotificationStatus: (data: any) => updateNotificationStatus(data),
  getPaginationNotification: (
    configUserRole?: number,
    pageBlockId?: number,
    noOfItems?: number,
    notificationTypeId?: number,
    pageNumber?: number
  ) =>
    getNotifications(
      undefined,
      pageBlockId,
      undefined,
      undefined,
      noOfItems,
      notificationTypeId || undefined,
      pageNumber
    ),
};

function mapStateToProps(state: any) {
  return {
    studentNotifications: state.notification.notifications,
    notificationType: state.notificationType.notificationTypes,
    snapShots: state.snapShot.snapShots,
    paginationNotification: state.notification.paginationNotification,
    notViewedCount: state.notificationCount.notificationCounts?.notViewedCount,
    currentUser: state.userDetails.userProfileData,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Notifications);
